'use client';

import React, {
  ChangeEvent,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { FacetHits, GetTopSearchesResponse, SearchForFacetValuesResponse, SearchResponse } from 'algoliasearch';
import { useRouter, useSearchParams } from 'next/navigation';

import { algoliaClient } from '@/config/algolia';
import { getClientEnv } from '@/config/clientEnv';
import { ALGOLIA_INDEX_NAME, ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME } from '@/config/env';
import useEzShip from '@/helpers/hooks/useEzShip';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { generateApproperiateURL, getRelativeUrlForInternalDomains } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { RootState, useAppDispatch, useAppSelector } from '@/store';
import {
  useCreateRecentSearchMutation,
  useDeleteRecentSearchMutation,
  useGetRecentSearchesQuery,
} from '@/store/api/recentSearch';
import { addSearchQuery, deleteRecentSearch, fetchRecentSearches } from '@/store/slices/recentSearch';
import { setLoading } from '@/store/slices/search';
import { AlgoliaProduct, RecentSearch } from '@/types/Algolia';
import {
  AutocompleteCollection,
  AutocompletePlugin,
  createAutocomplete,
  GetInputProps,
} from '@algolia/autocomplete-core';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin, search } from '@algolia/autocomplete-plugin-recent-searches';
// import { createLocalStorage, createRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { RecentSearchesItem } from '@algolia/autocomplete-plugin-recent-searches/dist/esm/types';
import { createRedirectUrlPlugin, RedirectUrlItem } from '@algolia/autocomplete-plugin-redirect-url';
import { getAlgoliaFacets, getAlgoliaResults, SearchClient } from '@algolia/autocomplete-preset-algolia';

export type QuerySuggestion = {
  nb_words: number;
  objectID: string;
  popularity: number;
  query: string;
};

export type CustomAutoCompleteState = {
  query: string;
  collections: AutocompleteCollection<
    | AlgoliaProduct
    | QuerySuggestion
    | RecentSearchesItem
    | GetTopSearchesResponse
    | RedirectUrlItem
    | SearchForFacetValuesResponse
  >[];
  isOpen: boolean;
  status?: 'loading' | 'idle' | 'stalled' | 'error';
};

export type QuickAccessProduct = {
  id: number;
  TileName: string;
  StartDate: string;
  EndDate: string;
  Order: number;
  MobileImageURL: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: null | string;
  DesktopAndTabletImageURL: string;
  TopSection: {
    value: string;
    loaded: boolean;
    visible: boolean;
  };
  BottomSection: {
    value: string;
    loaded: boolean;
    visible: boolean;
  };
  GreenBackgroundSection: {
    value: string;
    loaded: boolean;
    visible: boolean;
  };
  URL: {
    value: string;
    loaded: boolean;
    visible: boolean;
  };
};
interface SearchBarContextProps {
  value: string;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSearch: (searchTerm?: string) => void;
  autocompleteState: CustomAutoCompleteState;
  setAutocompleteState: (state: CustomAutoCompleteState) => void;
  closeAutoCompleteMenu: () => void;
  getInputProps: GetInputProps<Event, MouseEvent, KeyboardEvent>;
  setQuery: (q: string) => void;
  isSearchBarFocused: boolean;
  setSearchBarFocused: (b: boolean) => void;
  popularSearchTerms: GetTopSearchesResponse;
  algoliaCategories: Array<SearchForFacetValuesResponse>;
  setFocusedCategory: (category: string) => void;
  focusedCategory: string;
  algoliaProductsForFocusedCategory?: SearchResponse<AlgoliaProduct>;
  quickAccessList: QuickAccessProduct[];
  isQuickAccessListLoading: boolean;
  recentSearchList: RecentSearch[];
  addSearchQueryToRecentSearches: (value?: string, isLink?: boolean, link?: string) => void;
  deleteSearchQueryInRecentSearches: (id: string) => void;
  algoliaProductsForSearchQuery?: SearchResponse<AlgoliaProduct>;
  blurInputFields: () => void;
  setSearchValue: (value: string) => void;
  deadStateRef: React.MutableRefObject<boolean>;
  navigateToSearchPage: (value: string) => void;
}

interface SearchBarProviderProps {
  children: ReactNode;
}

const SearchBarContext = createContext<SearchBarContextProps>({
  value: '',
  onSearchChange: () => {},
  onSearch: () => {},
  autocompleteState: {
    query: '',
    collections: [],
    isOpen: false,
  },
  setAutocompleteState(state) {},
  closeAutoCompleteMenu() {},
  getInputProps: () => ({}) as any,
  setQuery(q) {},
  isSearchBarFocused: true,
  setSearchBarFocused(b) {},
  popularSearchTerms: { searches: [] },
  algoliaCategories: [],
  setFocusedCategory(category) {},
  focusedCategory: '',
  quickAccessList: [],
  isQuickAccessListLoading: false,
  recentSearchList: [],
  addSearchQueryToRecentSearches: value => {},
  deleteSearchQueryInRecentSearches: id => {},
  blurInputFields() {},
  setSearchValue: value => {},
  deadStateRef: { current: false },
  navigateToSearchPage: value => {},
});

const SearchBarProvider = ({ children }: SearchBarProviderProps) => {
  const [value, setValue] = useState('');
  const searchParams = useSearchParams();
  const { webalias } = useWebAlias();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isBackOffice } = useAppContext();
  const [algoliaRes, setAlgoliaRes] = useState<SearchResponse<AlgoliaProduct>>();
  const [focusedCategory, setFocusedCategory] = useState<string>('');
  const [isSearchBarFocused, setSearchBarFocused] = useState(false);
  const { ezShipId, generateURL: generateEzShipURL } = useEzShip();
  const searchTerm = decodeURIComponent(searchParams.get('query')?.trim() || '');

  const handleDeadStateRef = useRef<boolean>(false);

  const [algoliaProductsForFocusedCategory, setAlgoliaProductsForFocusedCategory] =
    useState<SearchResponse<AlgoliaProduct>>();
  const [autocompleteState, setAutocompleteState] = useState<CustomAutoCompleteState>({
    query: '',
    collections: [],
    isOpen: false,
  });

  const [popularSearchTerms, setPopularSearchTerms] = useState<GetTopSearchesResponse>({ searches: [] });
  const [isQuickAccessListLoading, seIsQuickAccessListLoading] = useState(false);
  const [quickAccessApiList, setQuickAccessApiList] = useState<QuickAccessProduct[]>([]);
  const [recentSearchList, setRecentSearchList] = useState<RecentSearch[]>([]);

  const { searches } = useSelector((state: RootState) => state.slices.recentSearch);
  const { isAuthenticated, isAuthenticationLoading } = useSelector(
    (state: RootState) => state.slices.authenticationSlice
  );
  const cookieConsent = useAppSelector(state => state.slices.appConfig.isCookieConsentRejected);

  const { data, refetch } = useGetRecentSearchesQuery(undefined, {
    skip: !isAuthenticated,
  });
  const [addSearchToRecentApi] = useCreateRecentSearchMutation();
  const [deleteRecentSearchApi] = useDeleteRecentSearchMutation();

  useEffect(() => {
    if (searchTerm?.length) setValue(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (!isSearchBarFocused) setFocusedCategory('');
  }, [isSearchBarFocused]);

  const getProductsForFocusedCategory = useCallback(async () => {
    try {
      const categoryKey = isBackOffice ? 'BackofficeCategoriesNames' : 'ReplicatedCategoriesNames';

      const filters = `Categories.Name:"${focusedCategory}"`;

      const algoliaRes = await algoliaClient.searchSingleIndex<AlgoliaProduct>({
        indexName: ALGOLIA_INDEX_NAME,
        searchParams: {
          query: value,
          ranking: ['custom'],
          enableRules: true,
          distinct: true,
          filters,
          clickAnalytics: cookieConsent === true,
          hitsPerPage: 4,
          ruleContexts: [isBackOffice ? 'backoffice' : 'replicated_site'],
        },
      });

      setAlgoliaProductsForFocusedCategory(algoliaRes);
    } catch (error) {
      console.error(error, 'error while fetching products for focused category');
    }
  }, [focusedCategory, isBackOffice, value, cookieConsent]);

  useEffect(() => {
    if (focusedCategory.length) getProductsForFocusedCategory();
  }, [focusedCategory, getProductsForFocusedCategory]);

  useEffect(() => {
    function listenForClickOutside() {
      try {
        document.body.addEventListener('click', e => {
          const target = e.target as Element;
          if (
            target.matches('#search-from-header') ||
            target.closest('.__autocomplete-search-results') ||
            target.matches('#search-from-menubar')
          )
            return;
          setSearchBarFocused(false);
        });
      } catch (error) {
        console.error(error, 'while listing outside click of search bar');
      }
    }
    listenForClickOutside();
  }, []);

  const unFocusInputsOnSearch = () => {
    try {
      document.getElementById('search-from-header')?.blur();
      document.getElementById('search-from-menubar')?.blur();
    } catch (error) {
      console.error('error while bluring search inputs', error);
    }
  };

  const popularPlugin = createQuerySuggestionsPlugin({
    searchClient: algoliaClient,
    indexName: ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
    getSearchParams() {
      return {
        query: '',
        hitsPerPage: 6,
      };
    },
    transformSource({ source }) {
      return {
        ...source,
        sourceId: 'popularPlugin',
        getItemInputValue({ item }) {
          return item.query;
        },
        onSelect({ setIsOpen }) {
          setIsOpen(true);
        },
      };
    },
  });

  // Quick Access Fetch
  const getQuickAccessTiles = useCallback(async () => {
    seIsQuickAccessListLoading(true);
    const { STRAPI_SERVER_URL } = getClientEnv();
    const result = await fetch(`${STRAPI_SERVER_URL}/pawtree/quickAccessTiles`, { method: 'GET' })
      .then(async response => {
        const data = await response.json();

        if (Array.isArray(data)) return data;

        return [];
      })
      .catch(error => {
        console.error({ error });
        seIsQuickAccessListLoading(false);
        return [];
      });

    seIsQuickAccessListLoading(false);
    setQuickAccessApiList(result);
  }, []);

  useEffect(() => {
    getQuickAccessTiles();
  }, []);

  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'autocomplete-two-column-layout-example',
    search(params) {
      return search({ ...params, limit: params.query ? 1 : 4 });
    },
  });

  const redirectUrlPlugin = createRedirectUrlPlugin();
  const querySuggestionsPlugin = useMemo(
    () =>
      createQuerySuggestionsPlugin({
        searchClient: algoliaClient,
        indexName: ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
      }),
    []
  );

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }, []);

  const handleSearch = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (searchTerm?: string) => {
      unFocusInputsOnSearch();
      // handleAddSearchQuery();

      const theValue = searchTerm || value;

      const redirectUrlsCollection = autocompleteState.collections.find(
        c => c.source.sourceId === 'redirectUrlPlugin'
      ) as AutocompleteCollection<RedirectUrlItem>;

      if (redirectUrlsCollection?.items?.length) {
        const links = redirectUrlsCollection?.items?.map(val => val.urls)?.flat(2);
        const firstLink = links[0];

        if (firstLink?.length > 0) {
          handleAddSearchQuery(value, true, getRelativeUrlForInternalDomains(firstLink, isBackOffice));
          router.push(getRelativeUrlForInternalDomains(firstLink, isBackOffice).replace('{0}', webalias));
          setValue('');
          return;
        }
      }

      const current = new URLSearchParams(
        Array.from(searchParams.entries())
          .filter(v => v[0] && v[0] !== 'query')
          .filter(v => v[0] && v[0] !== 'price')
          .filter(v => v[0] && v[0] !== 'itemCodes')
          .filter(v => v[0] && v[0] !== 'itemCode')
          .filter(v => v[0] && v[0] !== 'queryID')
          .filter(v => v[0] && v[0] !== 'pets')
          .filter(v => v[0] && v[0] !== 'rating')
          .filter(v => v[0] && v[0] !== 'innerItemCode')
      ); // -> has to use this form

      const autoOrderQueryParam = ezShipId.length ? `&autoOrderID=${ezShipId}` : '';
      const queryParams = current.toString() ? `&${current.toString()}` : '';
      if (!theValue || !theValue?.length) return;
      if (searchParams.get('query') === theValue) return;
      dispatch(setLoading(true));
      handleAddSearchQuery(theValue);
      router.push(
        generateApproperiateURL(
          `/products/search?query=${encodeURIComponent(theValue)}${queryParams}${autoOrderQueryParam}`,
          webalias
        )
      );
    },
    [value, autocompleteState.collections, searchParams, ezShipId, dispatch, router, webalias]
  );

  const closeAutoCompleteMenu = () => {
    setAutocompleteState(s => ({ ...s, isOpen: false }));
  };

  const popularCategoriesPlugin: AutocompletePlugin<SearchForFacetValuesResponse, {}> = useMemo(() => {
    return {
      getSources() {
        return [
          {
            sourceId: 'popularCategoriesPlugin',
            getItems() {
              return getAlgoliaFacets({
                searchClient: algoliaClient as unknown as SearchClient,
                queries: [
                  {
                    indexName: ALGOLIA_INDEX_NAME,
                    facet: isBackOffice ? 'BackofficeCategoriesNames' : 'ReplicatedCategoriesNames',
                    facetQuery: '',
                    maxFacetHits: 9,
                    type: 'facet',
                  },
                ],
              });
            },

            onSelect({ setIsOpen }) {
              setIsOpen(true);
            },
          },
        ];
      },
    };
  }, [isBackOffice]);

  // Recent Search Featrue Queries
  useEffect(() => {
    dispatch(fetchRecentSearches());
  }, [dispatch]);

  useEffect(() => {
    if (isSearchBarFocused && !isAuthenticationLoading) {
      const receSearches: any = data;
      setRecentSearchList(isAuthenticated ? receSearches?.data || [] : searches);
    }
  }, [dispatch, isSearchBarFocused, isAuthenticationLoading, searches, data, isAuthenticated]);

  const handleAddSearchQuery = useCallback(
    async (searchQuery?: string, isLink?: boolean, link?: string) => {
      if (handleDeadStateRef.current) return null;

      if (!value && !searchQuery) return null;
      if (autocompleteState.status !== 'idle' && !algoliaRes?.hits?.length) return null;

      if (isAuthenticated) {
        const res = await addSearchToRecentApi({
          searchItemKey: '',
          searchText: searchQuery ? searchQuery : value,
          isLink: isLink || false,
          link,
        }).unwrap();

        refetch();
        return console.log({ res });
      }
      dispatch(
        addSearchQuery({
          searchItemKey: '',
          searchText: searchQuery ? searchQuery : value,
          isLink: isLink || false,
          link,
        })
      );
      dispatch(fetchRecentSearches());
    },
    [value, isAuthenticated, addSearchToRecentApi, dispatch, autocompleteState, algoliaRes, refetch]
  );

  const handleDeleteSearchQuery = useCallback(
    async (id: string) => {
      if (isAuthenticated) {
        const res = await deleteRecentSearchApi(id).unwrap();
        refetch();
        return console.log({ res });
      }
      dispatch(deleteRecentSearch(id));
      dispatch(fetchRecentSearches());
    },
    [deleteRecentSearchApi, dispatch, isAuthenticated, refetch]
  );

  const setSearchValue = (value: string) => {
    setValue(value);
  };

  const handleNavigateToSearchPage = useCallback(
    (value: string) => {
      unFocusInputsOnSearch();

      const redirectUrlsCollection = autocompleteState.collections.find(
        c => c.source.sourceId === 'redirectUrlPlugin'
      ) as AutocompleteCollection<RedirectUrlItem>;

      if (redirectUrlsCollection?.items?.length) {
        const links = (redirectUrlsCollection?.items?.filter(item => item.sourceId == 'algoliaProducts') || [])
          .map((val: RedirectUrlItem) => val.urls)
          ?.flat(2);
        const firstLink = links[0];

        if (firstLink?.length > 0) {
          handleAddSearchQuery(value, true, getRelativeUrlForInternalDomains(firstLink, isBackOffice));
          router.push(getRelativeUrlForInternalDomains(firstLink, isBackOffice).replace('{0}', webalias));
          setValue('');
          return;
        }
      }

      handleAddSearchQuery();
      const current = new URLSearchParams(Array.from(searchParams.entries()).filter(v => v[0] && v[0] !== 'query')); // -> has to use this form
      const queryParams = current.toString() ? `&${current.toString()}` : '';
      const autoOrderQueryParam = ezShipId.length ? `&autoOrderID=${ezShipId}` : '';
      if (!value || !value?.length) return;
      if (searchParams.get('query') === value) return;
      dispatch(setLoading(true));
      router.push(
        generateApproperiateURL(
          `/products/search?query=${encodeURIComponent(value)}${queryParams}${autoOrderQueryParam}`,
          webalias
        )
      );
    },
    [handleAddSearchQuery, autocompleteState.collections, searchParams, ezShipId, dispatch, router, webalias]
  );

  const { getInputProps, setQuery } = useMemo(() => {
    return createAutocomplete<
      | AlgoliaProduct
      | QuerySuggestion
      | RecentSearchesItem
      | GetTopSearchesResponse
      | RedirectUrlItem
      | SearchForFacetValuesResponse
    >({
      plugins: [
        redirectUrlPlugin,
        recentSearchesPlugin,
        querySuggestionsPlugin,
        popularPlugin,
        popularCategoriesPlugin,
      ],
      openOnFocus: true,
      getSources(params) {
        return [
          {
            sourceId: 'algoliaProducts',
            getItems({ query }) {
              return getAlgoliaResults({
                searchClient: algoliaClient as unknown as SearchClient,
                queries: [
                  {
                    indexName: ALGOLIA_INDEX_NAME,
                    params: {
                      query,
                      hitsPerPage: 4,
                      clickAnalytics: cookieConsent === true,
                      distinct: true,
                      filters: `${
                        isBackOffice ? 'IsSearchableInBackoffice' : 'IsSearchableInReplicated'
                      }:true AND IsDonationItem:false`,
                    },
                  },
                ],
              });
            },
          },
          {
            sourceId: 'algoliaRes',
            getItems({ query }) {
              return getAlgoliaResults({
                searchClient: algoliaClient as unknown as SearchClient,
                queries: [
                  {
                    indexName: ALGOLIA_INDEX_NAME,

                    params: {
                      query: query,

                      clickAnalytics: cookieConsent === true,
                      distinct: true,
                      hitsPerPage: 100,
                      ruleContexts: [isBackOffice ? 'backoffice' : 'replicated_site', 'search_page'],
                    },
                  },
                ],
              });
            },
          },
          {
            sourceId: 'algoliaCategories',
            getItems({ query }) {
              return getAlgoliaFacets({
                searchClient: algoliaClient as unknown as SearchClient,
                queries: [
                  {
                    indexName: ALGOLIA_INDEX_NAME,
                    facet: isBackOffice ? 'BackofficeCategoriesNames' : 'ReplicatedCategoriesNames',
                    type: 'facet',

                    params: {
                      query: query,
                    },
                  },
                ],
              });
            },
          },
        ];
      },

      onStateChange({ state }) {
        setAutocompleteState(state);
      },
      onSubmit() {
        handleSearch();
      },
    });
  }, [
    handleSearch,
    isBackOffice,
    popularCategoriesPlugin,
    popularPlugin,
    querySuggestionsPlugin,
    recentSearchesPlugin,
    redirectUrlPlugin,
    cookieConsent,
  ]);

  const algoliaCategories = useMemo(
    () => [
      {
        facetHits: (
          autocompleteState.collections.find(c => c.source.sourceId === 'algoliaCategories')?.items || []
        ).map((i: any) => ({
          value: i.label,
          count: i.count,
          highlighted: i._highlightResult?.label?.value || i.label,
        })) as unknown as Array<FacetHits>,
        exhaustiveFacetsCount: false,
      },
    ],
    [autocompleteState.collections]
  );

  const values = useMemo(
    () => ({
      value,
      onSearchChange: handleSearchChange,
      onSearch: handleSearch,
      autocompleteState,
      setAutocompleteState: (state: SearchBarContextProps['autocompleteState']) => setAutocompleteState(state),
      closeAutoCompleteMenu,
      getInputProps,
      setQuery,
      isSearchBarFocused,
      setSearchBarFocused,
      popularSearchTerms,
      algoliaCategories,
      setFocusedCategory,
      focusedCategory,
      algoliaProductsForFocusedCategory,
      quickAccessList: quickAccessApiList,
      isQuickAccessListLoading,
      recentSearchList,
      addSearchQueryToRecentSearches: handleAddSearchQuery,
      deleteSearchQueryInRecentSearches: handleDeleteSearchQuery,
      algoliaProductsForSearchQuery: algoliaRes,
      blurInputFields: unFocusInputsOnSearch,
      setSearchValue,
      deadStateRef: handleDeadStateRef,
      navigateToSearchPage: handleNavigateToSearchPage,
    }),
    [
      value,
      handleSearchChange,
      handleSearch,
      autocompleteState,
      getInputProps,
      setQuery,
      isSearchBarFocused,
      popularSearchTerms,
      algoliaCategories,
      focusedCategory,
      algoliaProductsForFocusedCategory,
      quickAccessApiList,
      isQuickAccessListLoading,
      recentSearchList,
      handleAddSearchQuery,
      handleDeleteSearchQuery,
      algoliaRes,
      handleNavigateToSearchPage,
    ]
  );
  return <SearchBarContext.Provider value={values}>{children}</SearchBarContext.Provider>;
};

export const useSearchContext = () => {
  return useContext(SearchBarContext);
};

export default SearchBarProvider;
